import React, { useCallback, useEffect, useState } from 'react';
import { getApiBaseUrl } from '../utils/getApiUrl';

export type SegmentColor =
  | 'indigo'
  | 'amber'
  | 'orange'
  | 'emerald'
  | 'rose'
  | 'sky'
  | 'violet'
  | 'slate';

export interface ClubSegmentConfig {
  id: string;
  label: string;
  color: SegmentColor | string;
  enabled: boolean;
  min_visits: number;
  max_visits: number | null;
  min_months_since: number | null;
  max_months_since: number | null;
  min_days_since: number | null;
  max_days_since: number | null;
  services?: string[];
  service_types?: string[];
  hint?: string;
  count?: number;
}

interface ServiceOption {
  label: string;
  type?: string;
  count: number;
}

interface TypeOption {
  label: string;
  count: number;
}

const COLOR_OPTIONS: { key: SegmentColor; label: string }[] = [
  { key: 'indigo', label: 'نیلی' },
  { key: 'amber', label: 'کهربایی' },
  { key: 'orange', label: 'نارنجی' },
  { key: 'emerald', label: 'سبز' },
  { key: 'rose', label: 'صورتی' },
  { key: 'sky', label: 'آبی' },
  { key: 'violet', label: 'بنفش' },
  { key: 'slate', label: 'خاکستری' },
];

const toPersianDigits = (s: string): string =>
  s.replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

/** توضیح خودکار کارت از روی قوانین — هم‌تراز با segment_hint بک‌اند */
const buildAutoHint = (seg: ClubSegmentConfig): string => {
  const minV = seg.min_visits;
  const maxV = seg.max_visits;
  let visitPart = '';
  if (minV != null && maxV != null) {
    visitPart = minV === maxV ? `دقیقاً ${minV} پذیرش HIS` : `بین ${minV} تا ${maxV} پذیرش HIS`;
  } else if (minV != null) {
    visitPart = `حداقل ${minV} پذیرش HIS`;
  } else if (maxV != null) {
    visitPart = `حداکثر ${maxV} پذیرش HIS`;
  }

  const recency: string[] = [];
  if (seg.min_days_since != null) recency.push(`حداقل ${seg.min_days_since} روز بدون مراجعه`);
  if (seg.min_months_since != null) recency.push(`بیش از ${seg.min_months_since} ماه بدون مراجعه`);
  if (seg.max_months_since != null) recency.push(`آخرین مراجعه حداکثر ${seg.max_months_since} ماه پیش`);
  if (seg.max_days_since != null) recency.push(`آخرین مراجعه حداکثر ${seg.max_days_since} روز پیش`);

  const services = seg.services || [];
  const types = seg.service_types || [];
  let servicePart = '';
  if (services.length === 1) servicePart = `خدمت: ${services[0]}`;
  else if (services.length > 1) servicePart = `${services.length} خدمت انتخاب‌شده`;
  else if (types.length === 1) servicePart = `نوع خدمت: ${types[0]}`;
  else if (types.length > 1) servicePart = `${types.length} نوع خدمت`;

  const parts = [visitPart, recency.join(' و '), servicePart].filter(Boolean);
  if (!parts.length) return 'بدون محدودیت مشخص';
  return parts.length === 1 ? parts[0] : parts.join('؛ ');
};

const emptySegment = (index: number): ClubSegmentConfig => ({
  id: `segment_${index + 1}`,
  label: `سگمنت ${index + 1}`,
  color: COLOR_OPTIONS[index % COLOR_OPTIONS.length].key,
  enabled: true,
  min_visits: 2,
  max_visits: null,
  min_months_since: null,
  max_months_since: null,
  min_days_since: null,
  max_days_since: null,
  services: [],
  service_types: [],
  hint: '',
});

const normalizeSeg = (s: ClubSegmentConfig): ClubSegmentConfig => ({
  ...s,
  services: Array.isArray(s.services) ? [...s.services] : [],
  service_types: Array.isArray(s.service_types) ? [...s.service_types] : [],
  hint: typeof s.hint === 'string' ? s.hint : '',
});

const toNullableNumber = (raw: string): number | null => {
  const t = raw.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

interface Props {
  open: boolean;
  initial: ClubSegmentConfig[];
  defaults: ClubSegmentConfig[];
  minSegments: number;
  maxSegments: number;
  saving: boolean;
  error?: string | null;
  period?: string;
  authHeaders?: () => HeadersInit;
  onClose: () => void;
  onSave: (segments: ClubSegmentConfig[]) => void;
}

const CustomerClubSegmentsEditor: React.FC<Props> = ({
  open,
  initial,
  defaults,
  minSegments,
  maxSegments,
  saving,
  error,
  period = '12m',
  authHeaders,
  onClose,
  onSave,
}) => {
  const [draft, setDraft] = useState<ClubSegmentConfig[]>([]);
  const [types, setTypes] = useState<TypeOption[]>([]);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [catalogLoadedFor, setCatalogLoadedFor] = useState('');

  useEffect(() => {
    if (open) {
      setDraft(
        (initial.length ? initial : defaults).map((s) => normalizeSeg(s))
      );
    }
  }, [open, initial, defaults]);

  const fetchCatalog = useCallback(async () => {
    if (!open || !authHeaders) return;
    const key = period;
    if (catalogLoadedFor === key && (types.length || services.length)) return;
    setCatalogLoading(true);
    setCatalogError(null);
    try {
      const apiBaseUrl = getApiBaseUrl();
      const params = new URLSearchParams({ period, limit: '200' });
      const res = await fetch(`${apiBaseUrl}/admin/customer-club/services?${params}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('خطا در دریافت فهرست خدمات');
      const data = await res.json();
      setTypes(Array.isArray(data.types) ? data.types : []);
      setServices(Array.isArray(data.services) ? data.services : []);
      setCatalogLoadedFor(key);
    } catch (err: unknown) {
      setCatalogError(err instanceof Error ? err.message : 'خطا در فهرست خدمات');
    } finally {
      setCatalogLoading(false);
    }
  }, [authHeaders, catalogLoadedFor, open, period, services.length, types.length]);

  useEffect(() => {
    if (open) void fetchCatalog();
  }, [open, fetchCatalog]);

  if (!open) return null;

  const updateAt = (index: number, patch: Partial<ClubSegmentConfig>) => {
    setDraft((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeAt = (index: number) => {
    setDraft((prev) => (prev.length <= minSegments ? prev : prev.filter((_, i) => i !== index)));
  };

  const addSegment = () => {
    setDraft((prev) => (prev.length >= maxSegments ? prev : [...prev, emptySegment(prev.length)]));
  };

  const toggleType = (index: number, label: string) => {
    setDraft((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const selectedTypes = row.service_types || [];
        const nextTypes = selectedTypes.includes(label)
          ? selectedTypes.filter((x) => x !== label)
          : [...selectedTypes, label];
        const nextServices = (row.services || []).filter((svc) => {
          const svcRow = services.find((s) => s.label === svc);
          if (!svcRow?.type) return true;
          if (!nextTypes.length) return true;
          return nextTypes.includes(svcRow.type);
        });
        return { ...row, service_types: nextTypes, services: nextServices };
      })
    );
  };

  const toggleService = (index: number, label: string) => {
    setDraft((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const selected = row.services || [];
        const next = selected.includes(label)
          ? selected.filter((x) => x !== label)
          : [...selected, label];
        return { ...row, services: next };
      })
    );
  };

  const filteredServicesFor = (seg: ClubSegmentConfig) => {
    const selectedTypes = seg.service_types || [];
    if (!selectedTypes.length) return services;
    return services.filter((s) => selectedTypes.includes(s.type || ''));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-gray-900/50 p-3 sm:items-center sm:p-6">
      <div
        className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl border bg-white p-4 shadow-2xl sm:p-6"
        style={{ direction: 'rtl' }}
        role="dialog"
        aria-modal
        aria-label="ویرایش سگمنت‌ها"
      >
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-gray-900">شخصی‌سازی سگمنت‌ها</h3>
            <p className="mt-1 text-sm text-gray-500">
              بین {minSegments} تا {maxSegments} سگمنت بسازید و قوانین تعداد مراجعه، فاصله از آخرین
              مراجعه و در صورت نیاز فیلتر خدمت را خودتان تعیین کنید.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
          >
            بستن
          </button>
        </div>

        {error ? (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        ) : null}

        <div className="space-y-4">
          {draft.map((seg, index) => {
            const selectedTypes = seg.service_types || [];
            const selectedServices = seg.services || [];
            const filteredServices = filteredServicesFor(seg);
            return (
              <div key={`${seg.id}-${index}`} className="rounded-2xl border border-gray-200 bg-gray-50/70 p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-gray-800">سگمنت {index + 1}</p>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1.5 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={seg.enabled}
                        onChange={(e) => updateAt(index, { enabled: e.target.checked })}
                      />
                      فعال
                    </label>
                    <button
                      type="button"
                      disabled={draft.length <= minSegments}
                      onClick={() => removeAt(index)}
                      className="rounded-lg border border-red-100 bg-red-50 px-2.5 py-1 text-xs text-red-700 disabled:opacity-40"
                    >
                      حذف
                    </button>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block text-xs text-gray-600">
                    عنوان
                    <input
                      type="text"
                      value={seg.label}
                      onChange={(e) => updateAt(index, { label: e.target.value })}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="block text-xs text-gray-600">
                    شناسه (انگلیسی، برای API)
                    <input
                      type="text"
                      value={seg.id}
                      onChange={(e) => updateAt(index, { id: e.target.value })}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm [direction:ltr]"
                      dir="ltr"
                    />
                  </label>
                  <label className="block text-xs text-gray-600">
                    رنگ کارت
                    <select
                      value={seg.color}
                      onChange={(e) => updateAt(index, { color: e.target.value })}
                      className="select-rtl mt-1 w-full rounded-lg border border-gray-300 bg-white py-2 pr-3 text-sm"
                    >
                      {COLOR_OPTIONS.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block text-xs text-gray-600">
                      حداقل پذیرش
                      <input
                        type="number"
                        min={1}
                        value={seg.min_visits}
                        onChange={(e) =>
                          updateAt(index, { min_visits: Math.max(1, Number(e.target.value) || 1) })
                        }
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="block text-xs text-gray-600">
                      حداکثر پذیرش
                      <input
                        type="number"
                        min={1}
                        placeholder="بدون سقف"
                        value={seg.max_visits ?? ''}
                        onChange={(e) => updateAt(index, { max_visits: toNullableNumber(e.target.value) })}
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                      />
                    </label>
                  </div>
                </div>

                <div className="mt-3">
                  <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                    <label htmlFor={`segment-hint-${index}`} className="text-xs text-gray-600">
                      توضیح کارت
                    </label>
                    <button
                      type="button"
                      onClick={() => updateAt(index, { hint: toPersianDigits(buildAutoHint(seg)) })}
                      className="text-[11px] text-indigo-600 hover:underline"
                    >
                      تولید از قوانین
                    </button>
                  </div>
                  <textarea
                    id={`segment-hint-${index}`}
                    rows={2}
                    maxLength={240}
                    value={seg.hint || ''}
                    onChange={(e) => updateAt(index, { hint: e.target.value })}
                    placeholder={toPersianDigits(buildAutoHint(seg))}
                    className="w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm leading-relaxed text-gray-700"
                  />
                  <p className="mt-1 text-[11px] text-gray-400">
                    این متن زیر عدد کارت نمایش داده می‌شود. برای بازسازی از روی قوانین فعلی، «تولید از
                    قوانین» را بزنید.
                  </p>
                </div>

                <p className="mb-2 mt-4 text-xs font-medium text-gray-500">فاصله از آخرین مراجعه (اختیاری)</p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <label className="block text-xs text-gray-600">
                    حداقل ماه بدون مراجعه
                    <input
                      type="number"
                      min={0}
                      placeholder="—"
                      value={seg.min_months_since ?? ''}
                      onChange={(e) =>
                        updateAt(index, { min_months_since: toNullableNumber(e.target.value) })
                      }
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="block text-xs text-gray-600">
                    حداکثر ماه از آخرین مراجعه
                    <input
                      type="number"
                      min={0}
                      placeholder="—"
                      value={seg.max_months_since ?? ''}
                      onChange={(e) =>
                        updateAt(index, { max_months_since: toNullableNumber(e.target.value) })
                      }
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="block text-xs text-gray-600">
                    حداقل روز بدون مراجعه
                    <input
                      type="number"
                      min={0}
                      placeholder="—"
                      value={seg.min_days_since ?? ''}
                      onChange={(e) =>
                        updateAt(index, { min_days_since: toNullableNumber(e.target.value) })
                      }
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="block text-xs text-gray-600">
                    حداکثر روز از آخرین مراجعه
                    <input
                      type="number"
                      min={0}
                      placeholder="—"
                      value={seg.max_days_since ?? ''}
                      onChange={(e) =>
                        updateAt(index, { max_days_since: toNullableNumber(e.target.value) })
                      }
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                    />
                  </label>
                </div>

                <div className="mt-4 border-t border-gray-200/80 pt-4">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-xs font-medium text-gray-500">فیلتر خدمت (اختیاری)</p>
                      <p className="mt-0.5 text-[11px] text-gray-400">
                        دقیقاً مثل گزارش‌ساز؛ اگر خدمت جزئی انتخاب شود فقط همان اعمال می‌شود.
                      </p>
                    </div>
                    {selectedServices.length > 0 || selectedTypes.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => updateAt(index, { services: [], service_types: [] })}
                        className="text-xs text-indigo-600 hover:underline"
                      >
                        پاک کردن فیلتر خدمت
                      </button>
                    ) : null}
                  </div>

                  {catalogLoading ? (
                    <p className="py-4 text-center text-xs text-gray-500">در حال بارگذاری فهرست خدمات…</p>
                  ) : catalogError ? (
                    <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      {catalogError}
                    </p>
                  ) : (
                    <div className="grid gap-3 lg:grid-cols-2">
                      <div>
                        <p className="mb-1.5 text-xs font-semibold text-gray-700">نوع خدمت</p>
                        <div className="max-h-40 space-y-1 overflow-y-auto rounded-xl border border-gray-100 bg-white p-2">
                          {types.length === 0 ? (
                            <p className="px-2 py-4 text-center text-[11px] text-gray-500">
                              نوعی در این بازه نیست.
                            </p>
                          ) : (
                            types.map((t) => (
                              <label
                                key={t.label}
                                className="flex cursor-pointer items-center justify-between gap-2 rounded-lg px-2 py-1 text-xs hover:bg-gray-50"
                              >
                                <span className="flex min-w-0 items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={selectedTypes.includes(t.label)}
                                    onChange={() => toggleType(index, t.label)}
                                    className="rounded border-gray-300 text-indigo-600"
                                  />
                                  <span className="truncate">{t.label}</span>
                                </span>
                                <span className="shrink-0 tabular-nums text-[10px] text-gray-400">
                                  {t.count.toLocaleString('fa-IR')}
                                </span>
                              </label>
                            ))
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="mb-1.5 text-xs font-semibold text-gray-700">خدمت‌ها</p>
                        <div className="max-h-40 space-y-1 overflow-y-auto rounded-xl border border-gray-100 bg-white p-2">
                          {filteredServices.length === 0 ? (
                            <p className="px-2 py-4 text-center text-[11px] text-gray-500">خدمتی نیست.</p>
                          ) : (
                            filteredServices.map((s) => (
                              <label
                                key={s.label}
                                className="flex cursor-pointer items-center justify-between gap-2 rounded-lg px-2 py-1 text-xs hover:bg-gray-50"
                              >
                                <span className="flex min-w-0 items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={selectedServices.includes(s.label)}
                                    onChange={() => toggleService(index, s.label)}
                                    className="shrink-0 rounded border-gray-300 text-indigo-600"
                                  />
                                  <span className="truncate">{s.label}</span>
                                </span>
                                <span className="shrink-0 tabular-nums text-[10px] text-gray-400">
                                  {s.count.toLocaleString('fa-IR')}
                                </span>
                              </label>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  <p className="mt-2 text-[11px] text-gray-400">
                    {selectedServices.length
                      ? `${toPersianDigits(String(selectedServices.length))} خدمت انتخاب‌شده`
                      : selectedTypes.length
                        ? `${toPersianDigits(String(selectedTypes.length))} نوع انتخاب‌شده`
                        : 'بدون فیلتر خدمت'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={draft.length >= maxSegments}
              onClick={addSegment}
              className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-800 disabled:opacity-40"
            >
              + افزودن سگمنت
            </button>
            <button
              type="button"
              onClick={() => setDraft(defaults.map((s) => normalizeSeg(s)))}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              بازگشت به پیش‌فرض
            </button>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700"
            >
              انصراف
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => onSave(draft)}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {saving ? 'در حال ذخیره…' : 'ذخیره سگمنت‌ها'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerClubSegmentsEditor;
